import { useMutation, useQuery } from '@apollo/client';
import { Box, Typography, Stack, Button, FormControl, FormLabel, Select, Option, CircularProgress, Alert, Radio, RadioGroup, Chip } from '@mui/joy';
import { Card, CardContent } from '@mui/joy';
import { useMemo, useState } from 'react';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import { Link as LinkIcon, LinkOff as UnlinkIcon, PersonAdd as ImportIcon, Edit as EditIcon } from '@mui/icons-material';
import ATSCandidatesList from './ATSCandidatesList';
import ATSCandidateImportDialog from './ATSCandidateImportDialog';
import ME from '../../gql/common/me.query';
import { getProviderQueries } from './providerQueryMap';
import { getProvider } from './providers';

const ATSJobLinker = ({ job, providerId = 'ashby' }) => {
  const jobId = job?._id;
  const [showCandidates, setShowCandidates] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedSourceStageId, setSelectedSourceStageId] = useState(null);
  const [selectedTargetStageId, setSelectedTargetStageId] = useState(null);
  const [isEditingSourceStage, setIsEditingSourceStage] = useState(false);
  const [editSourceStageId, setEditSourceStageId] = useState(null);
  const [isEditingTargetStage, setIsEditingTargetStage] = useState(false);
  const [editTargetStageId, setEditTargetStageId] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const feedback = useFeedback();

  const pq = getProviderQueries(providerId);
  const provider = getProvider(providerId);

  // Assessment mode flags (works for both Ashby and Greenhouse)
  const hasAssessmentSupport = !!pq.flowModeField;
  const flowMode = job?.[pq.flowModeField] || pq.defaultFlowMode;
  const isAssessmentOnly = hasAssessmentSupport && flowMode === 'assessment_only';

  // Initialize selectedMode from provider default
  const effectiveSelectedMode = selectedMode ?? pq.defaultFlowMode;

  // Check if ATS is connected
  const { data: statusData, error: statusError } = useQuery(pq.GET_STATUS, {
    onError: (error) => {
      console.error(`Error fetching ${provider.name} status:`, error);
    }
  });
  const isConnected = statusData?.[pq.statusField]?.enabled && !statusError;

  // Fetch ATS jobs
  const { data: jobsData, loading: loadingJobs, error: jobsError, refetch: refetchJobs } = useQuery(pq.LIST_JOBS, {
    skip: !isConnected,
    onError: (error) => {
      console.error(`Error fetching ${provider.name} jobs:`, error);
    }
  });

  // Find linked job
  const linkedJob = useMemo(() => {
    if (!jobsData?.[pq.jobsField] || !jobId) return null;
    return jobsData[pq.jobsField].find(j => j.linkedCavalryJobId === jobId);
  }, [jobsData, jobId, pq.jobsField]);

  const atsSourceStageId = job.atsSourceStageId;
  const atsTargetStageId = job.atsTargetStageId;

  // Fetch interview stages for selected job or linked job (to display stage name)
  const stagesJobId = selectedJobId || linkedJob?.id;
  const { data: stagesData, loading: loadingStages } = useQuery(pq.LIST_INTERVIEW_STAGES, {
    variables: { [pq.jobIdVar]: stagesJobId },
    skip: !stagesJobId,
    onError: (error) => {
      console.error('Error fetching interview stages:', error);
    }
  });

  const stages = useMemo(() => {
    const allStages = stagesData?.[pq.stagesField] || [];
    return [...allStages].sort((a, b) => (a[pq.stageSortField] ?? 0) - (b[pq.stageSortField] ?? 0));
  }, [stagesData, pq.stagesField, pq.stageSortField]);

  // Link mutation
  const [linkJob, { loading: linking }] = useMutation(pq.LINK_JOB, {
    refetchQueries: [{ query: pq.LIST_JOBS }, { query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      feedback.snackbar({ text: "Job linked successfully!", type: "success" });
      setSelectedJobId(null);
      setSelectedSourceStageId(null);
      setSelectedTargetStageId(null);
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  // Update source stage mutation (reuses link mutation)
  const [updateSourceStage, { loading: updatingSourceStage }] = useMutation(pq.LINK_JOB, {
    refetchQueries: [{ query: pq.LIST_JOBS }, { query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      feedback.snackbar({ text: "Source stage updated successfully!", type: "success" });
      setIsEditingSourceStage(false);
      setEditSourceStageId(null);
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  // Update target stage mutation (reuses link mutation)
  const [updateTargetStage, { loading: updatingTargetStage }] = useMutation(pq.LINK_JOB, {
    refetchQueries: [{ query: pq.LIST_JOBS }, { query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      feedback.snackbar({ text: "Target stage updated successfully!", type: "success" });
      setIsEditingTargetStage(false);
      setEditTargetStageId(null);
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  // Get stage name by ID
  const getStageName = (stageId) => {
    if (!stageId)
      return 'Not set';
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || 'Unknown stage';
  };

  // Unlink mutation
  const [unlinkJob, { loading: unlinking }] = useMutation(pq.UNLINK_JOB, {
    refetchQueries: [{ query: pq.LIST_JOBS }],
    onCompleted: () => {
      feedback.snackbar({ text: "Job unlinked successfully!", type: "success" });
      setShowCandidates(false);
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  const handleUnlink = async () => {
    const confirmed = await feedback.confirm({
      title: 'Unlink ATS Job?',
      description: 'This will remove the link between this job and the ATS job. You can re-link at any time.',
    });
    if (confirmed) {
      unlinkJob({ variables: { cavalryJobId: jobId } });
    }
  };

  // Don't render if not connected or is a new job
  if (!isConnected || !jobId || jobId === "new") {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography level="title-md" sx={{ mb: 1 }}>ATS Integration</Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Link this job to an ATS job and sync candidates
        </Typography>

        {linkedJob ? (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'success.softBg', borderRadius: 'sm' }}>
              <LinkIcon color="success" />
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm">Linked to: {linkedJob.title}</Typography>
                <Typography level="body-xs" color="neutral">
                  {linkedJob.departmentName && `${linkedJob.departmentName} • `}
                  {linkedJob[pq.locationField] && `${linkedJob[pq.locationField]} • `}
                  Status: {linkedJob.status}
                </Typography>
                {hasAssessmentSupport && (
                  <Chip size="sm" variant="soft" color={isAssessmentOnly ? 'primary' : 'neutral'} sx={{ mt: 0.5 }}>
                    Mode: {isAssessmentOnly ? pq.assessmentModeName : pq.importModeName}
                  </Chip>
                )}
              </Box>
              <Button
                size="sm"
                variant="plain"
                color="danger"
                startDecorator={<UnlinkIcon />}
                loading={unlinking}
                onClick={handleUnlink}
              >
                Unlink
              </Button>
            </Box>

            {/* Source Stage Display/Edit */}
            {!isAssessmentOnly && (
              <Box sx={{ p: 2, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
                {isEditingSourceStage ? (
                  <Stack spacing={1}>
                    <FormControl size="sm">
                      <FormLabel>Source Stage (when candidates are automatically imported)</FormLabel>
                      {loadingStages ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size="sm" />
                          <Typography level="body-sm">Loading stages...</Typography>
                        </Box>
                      ) : (
                        <Select
                          size="sm"
                          placeholder="Select source stage"
                          value={editSourceStageId}
                          onChange={(_, value) => setEditSourceStageId(value)}
                        >
                          {stages.map((stage) => (
                            <Option key={stage.id} value={stage.id}>
                              {stage.name}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="solid"
                        loading={updatingSourceStage}
                        onClick={() => updateSourceStage({
                          variables: {
                            [pq.jobIdVar]: linkedJob.id,
                            cavalryJobId: jobId,
                            sourceStageId: editSourceStageId,
                            targetStageId: atsTargetStageId
                          }
                        })}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                          setIsEditingSourceStage(false);
                          setEditSourceStageId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-xs" color="neutral">Source Stage</Typography>
                      <Typography level="body-sm">
                        {atsSourceStageId
                          ? (stages.length > 0 ? getStageName(atsSourceStageId) : 'Loading...')
                          : 'Not set'}
                      </Typography>
                    </Box>
                    <Button
                      size="sm"
                      variant="plain"
                      startDecorator={<EditIcon />}
                      onClick={() => {
                        setIsEditingSourceStage(true);
                        setEditSourceStageId(atsSourceStageId || null);
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Target Stage Display/Edit */}
            {!isAssessmentOnly && (
              <Box sx={{ p: 2, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
                {isEditingTargetStage ? (
                  <Stack spacing={1}>
                    <FormControl size="sm">
                      <FormLabel>Target Stage (after interview completion)</FormLabel>
                      {loadingStages ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size="sm" />
                          <Typography level="body-sm">Loading stages...</Typography>
                        </Box>
                      ) : (
                        <Select
                          size="sm"
                          placeholder="Select target stage"
                          value={editTargetStageId}
                          onChange={(_, value) => setEditTargetStageId(value)}
                        >
                          {stages.map((stage) => (
                            <Option key={stage.id} value={stage.id}>
                              {stage.name}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="solid"
                        loading={updatingTargetStage}
                        onClick={() => updateTargetStage({
                          variables: {
                            [pq.jobIdVar]: linkedJob.id,
                            cavalryJobId: jobId,
                            sourceStageId: atsSourceStageId,
                            targetStageId: editTargetStageId
                          }
                        })}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                          setIsEditingTargetStage(false);
                          setEditTargetStageId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-xs" color="neutral">Target Stage</Typography>
                      <Typography level="body-sm">
                        {atsTargetStageId
                          ? (stages.length > 0 ? getStageName(atsTargetStageId) : 'Loading...')
                          : 'Not set'}
                      </Typography>
                    </Box>
                    <Button
                      size="sm"
                      variant="plain"
                      startDecorator={<EditIcon />}
                      onClick={() => {
                        setIsEditingTargetStage(true);
                        setEditTargetStageId(atsTargetStageId || null);
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {isAssessmentOnly && (
              <Alert color="neutral" variant="soft">
                Candidate import is disabled for this job because it uses {provider.name} Assessment. Candidates will be created automatically when {provider.name} sends the assessment.
              </Alert>
            )}

            {!isAssessmentOnly && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<ImportIcon />}
                  onClick={() => setShowImportDialog(true)}
                >
                  Import Candidates
                </Button>
              </Stack>
            )}

            {!isAssessmentOnly && (
              <ATSCandidatesList
                cavalryJobId={jobId}
                show={showCandidates}
                onToggle={() => setShowCandidates(!showCandidates)}
                providerId={providerId}
              />
            )}

            {!isAssessmentOnly && (
              <ATSCandidateImportDialog
                open={showImportDialog}
                onClose={() => setShowImportDialog(false)}
                atsJobId={linkedJob.id}
                cavalryJobId={jobId}
                providerId={providerId}
              />
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {loadingJobs ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm">Loading ATS jobs...</Typography>
              </Box>
            ) : jobsError ? (
              <Box sx={{ p: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
                <Typography level="body-sm" color="warning">
                  Failed to load ATS jobs. Please try again. Details: {parseGraphQLError(jobsError)}
                </Typography>
                <Button size="sm" variant="soft" color="warning" sx={{ mt: 1 }} onClick={() => refetchJobs()}>
                  Retry
                </Button>
              </Box>
            ) : (
              <>
                <FormControl>
                  <FormLabel>Select ATS Job to Link</FormLabel>
                  <Select
                    placeholder="Choose a job..."
                    value={selectedJobId}
                    onChange={(_, value) => {
                      setSelectedJobId(value);
                      setSelectedSourceStageId(null);
                      setSelectedTargetStageId(null);
                    }}
                  >
                    {(jobsData?.[pq.jobsField] || [])
                      .filter(j => !j.linkedCavalryJobId)
                      .map((job) => (
                        <Option key={job.id} value={job.id}>
                          {job.title} {job.departmentName && `(${job.departmentName})`} - {job.status}
                        </Option>
                      ))}
                  </Select>
                </FormControl>
                {hasAssessmentSupport && selectedJobId && (
                  <FormControl>
                    <FormLabel>Integration Mode</FormLabel>
                    <RadioGroup
                      value={effectiveSelectedMode}
                      onChange={(e) => {
                        setSelectedMode(e.target.value);
                        if (e.target.value === 'assessment_only') {
                          setSelectedSourceStageId(null);
                          setSelectedTargetStageId(null);
                        }
                      }}
                    >
                      <Radio value={pq.defaultFlowMode} label={pq.importModeName} />
                      <Radio value="assessment_only" label={pq.assessmentModeName} />
                    </RadioGroup>
                  </FormControl>
                )}
                {hasAssessmentSupport && selectedJobId && effectiveSelectedMode === 'assessment_only' && (
                  <Alert color="neutral" variant="soft">
                    Assessment Only: candidates will be created by {provider.name} Assessment when the assessment is triggered. Manual import and webhook auto-import are disabled for this job.
                  </Alert>
                )}
                {hasAssessmentSupport && selectedJobId && effectiveSelectedMode === 'assessment_only' && !(() => {
                  try { return JSON.parse(job?.interviewQuestions || '[]').length > 0; } catch { return false; }
                })() && (
                    <Alert color="warning" variant="soft">
                      This job will not be available in {provider.name} Assessment until interview questions are configured.
                    </Alert>
                  )}
                {selectedJobId && effectiveSelectedMode !== 'assessment_only' && (
                  <FormControl>
                    <FormLabel>Source Stage (candidates to import)</FormLabel>
                    {loadingStages ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm">Loading stages...</Typography>
                      </Box>
                    ) : (
                      <Select
                        placeholder="Select source stage (optional)"
                        value={selectedSourceStageId}
                        onChange={(_, value) => setSelectedSourceStageId(value)}
                      >
                        {stages.map((stage) => (
                          <Option key={stage.id} value={stage.id}>
                            {stage.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}
                {selectedJobId && effectiveSelectedMode !== 'assessment_only' && (
                  <FormControl>
                    <FormLabel>Target Stage (after interview completion)</FormLabel>
                    {loadingStages ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm">Loading stages...</Typography>
                      </Box>
                    ) : (
                      <Select
                        placeholder="Select target stage (optional)"
                        value={selectedTargetStageId}
                        onChange={(_, value) => setSelectedTargetStageId(value)}
                      >
                        {stages.map((stage) => (
                          <Option key={stage.id} value={stage.id}>
                            {stage.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<LinkIcon />}
                  loading={linking}
                  disabled={!selectedJobId}
                  onClick={() => linkJob({
                    variables: {
                      [pq.jobIdVar]: selectedJobId,
                      cavalryJobId: jobId,
                      mode: hasAssessmentSupport ? effectiveSelectedMode : undefined,
                      sourceStageId: effectiveSelectedMode === 'assessment_only' ? null : selectedSourceStageId,
                      targetStageId: effectiveSelectedMode === 'assessment_only' ? null : selectedTargetStageId
                    }
                  })}
                >
                  Link Job
                </Button>
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ATSJobLinker;
